// @system — Modal/Dialog component (wraps shadcn Dialog)
// Accessible modal with backdrop, animations, and mobile-responsive design
// Mobile-first: Full-height on mobile, centered dialog on desktop
//
// Usage:
// <Modal open={isOpen} onClose={() => setOpen(false)} title="Title">
//   <p>Content</p>
// </Modal>

import { cn } from '@/app/lib/@system/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog'

/**
 * @typedef {'sm' | 'md' | 'lg' | 'xl' | 'full'} ModalSize
 *
 * @typedef {object} ModalProps
 * @property {boolean} open - Modal open state
 * @property {() => void} [onClose] - Close handler
 * @property {string} [title] - Modal title
 * @property {string} [description] - Modal description
 * @property {React.ReactNode} [children] - Modal content
 * @property {React.ReactNode} [footer] - Modal footer content
 * @property {ModalSize} [size='md'] - Modal size
 * @property {string} [className] - Additional CSS classes
 */

/**
 * Modal — Accessible modal dialog component (shadcn Dialog)
 * @param {ModalProps} props
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}) {
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    full: 'sm:max-w-[calc(100vw-2rem)]',
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent className={cn(sizeClasses[size], className)}>
        {(title || description) && (
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        <div className="overflow-y-auto max-h-[60vh] sm:max-h-[70vh]">
          {children}
        </div>
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  )
}

/**
 * @typedef {object} ConfirmModalProps
 * @property {boolean} open - Modal open state
 * @property {() => void} [onClose] - Close handler
 * @property {() => void} onConfirm - Confirm handler
 * @property {string} title - Confirmation title
 * @property {string} [description] - Confirmation message
 * @property {string} [confirmText='Confirm'] - Confirm button text
 * @property {string} [cancelText='Cancel'] - Cancel button text
 * @property {'default' | 'destructive'} [variant='default'] - Button variant
 * @property {boolean} [loading=false] - Loading state
 */

/**
 * ConfirmModal — Pre-configured confirmation modal (shadcn AlertDialog)
 * Mobile-optimized: stacked buttons on mobile, horizontal on desktop
 * @param {ConfirmModalProps} props
 */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
}) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={loading}
            className={variant === 'destructive' ? 'bg-[var(--color-error)] text-[var(--color-error)]-foreground hover:bg-[var(--color-error)]/90' : ''}
          >
            {loading ? 'Loading...' : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
